import React from 'react';
import styled from 'styled-components';
import { articleMarkdown } from '../../Markdown';
import {
  categoryTrackLabel,
  categoryUrl,
  ChevronLink,
  FaqArticle,
  FaqCategory,
  HELP_LIST_TRACK_CAT,
  SearchBarContainer,
} from '../util';
import { ContentBody, ContentTitle } from './common';
import {
  IconChevronLeft,
  spacer8,
  spacer16,
  spacer24,
  spacer32,
  spacer48,
  screenXsMax,
  body1FontSize,
  body1FontWeight,
  body1LineHeight,
  heading2FontSize,
} from '@spotify-internal/encore-web';
import { SearchBar } from '../../Search';
import { useT } from '@spotify-internal/i18n-core';
import { getLinkStyle } from '../../ui';
import { ArticleFeedbackModule } from '../../Module/ArticleFeedbackModule';

const Content = styled(ContentBody)`
  max-width: 800px;
`;

export const helpArticleTextStyle = `
  font-size: ${body1FontSize};
  font-weight: ${body1FontWeight};
  line-height: ${body1LineHeight};
`;

const Subsection = styled.div`
  @media (max-width: ${screenXsMax}) {
    color: ${({ theme }) => theme.help.grayBox.text};
    background-color: ${({ theme }) => theme.help.grayBox.background};
    padding: ${spacer16};
    margin-bottom: ${spacer16};
    border-radius: ${spacer8};
  }
`;

const Body = styled.div`
  a {
    ${props => getLinkStyle(props)}
    text-decoration: underline;

    &:hover:not(:focus):not(:disabled) {
      color: ${({ theme }) => theme.help.link.hover};
    }
  }
  h2 {
    font-size: ${heading2FontSize};
    padding-top: ${spacer16};
    padding-bottom: 0.5em !important;
  }

  h3 {
    padding-bottom: ${spacer16};
  }

  ul,
  ol {
    margin: auto;
    padding-left: ${spacer48};
  }

  ol > li,
  ul > li {
    ${helpArticleTextStyle}
    display: revert;
    padding-bottom: ${spacer16};
  }

  p {
    ${helpArticleTextStyle}
    margin: auto;
    max-width: unset;
  }

  p {
    padding-bottom: ${spacer16};
  }

  img {
    width: 100%;
  }

  table {
    margin-bottom: ${spacer32};
  }
`;

const Back = styled.div`
  a {
    ${props => getLinkStyle(props)}
  }
  padding-bottom: ${spacer24};
  svg {
    [dir='rtl'] & {
      transform: rotateY(180deg);
    }
  }
`;

interface FaqArticleViewType {
  article: FaqArticle;
  category?: FaqCategory;
}

export const FaqArticleView: React.FC<FaqArticleViewType> = ({
  article,
  category,
}) => {
  const t = useT();
  const text = article.answer;
  const subsections = [text].map(md => articleMarkdown(md, false));

  return (
    <Content>
      <Back>
        <ChevronLink
          aria-label={t(
            'FRODOR_CONTENT_NAVIGATION_LINK_BACK',
            'Back',
            'Back means previous in this case',
          )}
          track={[
            HELP_LIST_TRACK_CAT,
            `Back to ${categoryTrackLabel(category)}`,
          ]}
          href={categoryUrl(category)}
        >
          <IconChevronLeft aria-hidden="true" />
        </ChevronLink>
      </Back>

      <SearchBarContainer data-testid="help-search-bar">
        <SearchBar
          contentTypes={['faq']}
          placeholder={t(
            'FRODOR_SEARCH_BOX_PLACEHOLDER',
            'Search help articles',
            '',
          )}
          suggestCount={5}
          trackCategory={HELP_LIST_TRACK_CAT}
          getSuggestUrl={sug =>
            `${sug.url}?ref=sug_${HELP_LIST_TRACK_CAT}${
              category ? `&category=${category.urlSlug}` : ''
            }`
          }
        />
      </SearchBarContainer>

      <ContentTitle>{article.question}</ContentTitle>
      <Body>
        {subsections.map((section, index) => (
          <Subsection
            key={index}
            dangerouslySetInnerHTML={{ __html: section }}
          />
        ))}
      </Body>
      <ArticleFeedbackModule />
    </Content>
  );
};
